/**
 * Client photos service.
 *
 * Wired to the BE `/client-photo` endpoints (lib/module/v2/clientPhoto).
 * Public function shapes preserved from the previous stub so the
 * actions / reducers / components don't need to change:
 *   fetchClientPhotos(clientId, { cursor })
 *     → { photos, nextCursor, consent }
 *   uploadClientPhoto(clientId, file, { bookingId, tags })
 *     → { photo }
 *   patchClientPhoto(clientId, photoId, { tags }) → { photo }
 *   deleteClientPhoto(clientId, photoId) → { success: true }
 *   setPhotoConsent(clientId)             → { consent }
 *   revokePhotoConsent(clientId)          → { success, deletedCount? }
 *   fetchSalonTags()                      → { tags: [{name, count}] }
 *   fetchTagsForService(serviceName)      → { service, tags: [...] }
 *   setShareConsent(photoId, value)       → { photo }   value: none | noFace | social
 *   setSocialConsent(clientId)            → { consent } client-wide yes to social media
 *   revokeSocialConsent(clientId)         → { consent }
 *   searchSalonPhotos({ tag, consent, cursor, limit })
 *                                          → { photos, nextCursor }  newest first, whole salon
 *
 * Share consent (may a photo go on social media) is its own answer with who
 * and when recorded by the server. A photo's own answer wins; until it has
 * one it follows the client-wide answer (consent.social). The old `social`
 * TAG is not consent. effectiveShareConsent() below is the one reading of it.
 *
 * Cursor format on the wire: the BE returns
 *   { rows, hasMore, nextCursor: { capturedAt, _id } }
 * The FE flattens this to a single opaque string ("at|id") so the
 * action layer can keep treating cursor as a black-box token.
 */

import axios from "axios";
import API from "../config/API";

const headers = (extra = {}) => {
  const accessToken = localStorage.getItem("accessToken");
  return {
    "Content-Type": "application/json",
    authorization: "Basic YmV1YXBwOmJldWFwcA==",
    accessToken: `${accessToken}`,
    ...extra,
  };
};

const unwrap = (res) =>
  res?.data?.responseData?.result ||
  res?.data?.responseData ||
  res?.data?.data ||
  {};

// The API answers errors with HTTP 200 and statusCode 0. Consent writes must
// not look like they worked when they didn't, so the newer calls use this.
const unwrapStrict = (res) => {
  const d = res?.data;
  if (d && d.statusCode === 0) {
    const e = new Error(d.error?.responseMessage || d.error?.message || "Request failed");
    e.reason = d.error?.reason;
    throw e;
  }
  return unwrap(res);
};

const encodeCursor = (cursorObj) => {
  if (!cursorObj || !cursorObj.capturedAt || !cursorObj._id) return null;
  return `${new Date(cursorObj.capturedAt).toISOString()}|${cursorObj._id}`;
};

const decodeCursor = (cursorStr) => {
  if (!cursorStr || typeof cursorStr !== "string") return null;
  const [at, id] = cursorStr.split("|");
  if (!at || !id) return null;
  return { capturedAt: at, _id: id };
};

const STUB_PAGE_SIZE = 20;

async function fetchClientPhotos(clientId, { cursor = null, limit = STUB_PAGE_SIZE } = {}) {
  const cursorObj = decodeCursor(cursor);
  const params = { limit };
  if (cursorObj) {
    params.cursorAt = cursorObj.capturedAt;
    params.cursorId = cursorObj._id;
  }
  const res = await axios.get(API.CLIENT_PHOTO_LIST + clientId, {
    headers: headers(),
    params,
  });
  const payload = unwrap(res);
  const rows = Array.isArray(payload.rows) ? payload.rows : [];
  return {
    photos: rows.map((p) => ({ ...p, id: p._id || p.id })),
    nextCursor: encodeCursor(payload.nextCursor),
    consent: payload.consent || { givenAt: null, givenByUserId: null },
  };
}

async function uploadClientPhoto(clientId, file, { bookingId = null, tags = [] } = {}) {
  const form = new FormData();
  form.append("photo", file);
  if (bookingId) form.append("bookingId", bookingId);
  if (Array.isArray(tags) && tags.length > 0) {
    form.append("tags", JSON.stringify(tags));
  }
  const res = await axios.post(API.CLIENT_PHOTO_UPLOAD + clientId, form, {
    headers: headers({ "Content-Type": "multipart/form-data" }),
  });
  const payload = unwrap(res);
  // BE returns the created mongoose doc — normalise id field for the FE.
  const photo = payload && payload._id ? { ...payload, id: payload._id } : payload;
  return { photo };
}

async function patchClientPhoto(clientId, photoId, { tags } = {}) {
  const res = await axios.patch(
    API.CLIENT_PHOTO_ITEM + photoId,
    { tags: Array.isArray(tags) ? tags : [] },
    { headers: headers() }
  );
  const payload = unwrap(res);
  const photo = payload && payload._id ? { ...payload, id: payload._id } : payload;
  return { photo };
}

async function deleteClientPhoto(clientId, photoId) {
  await axios.delete(API.CLIENT_PHOTO_ITEM + photoId, { headers: headers() });
  return { success: true };
}

async function setPhotoConsent(clientId) {
  const res = await axios.post(
    API.CLIENT_PHOTO_CONSENT + clientId + "/consent",
    {},
    { headers: headers() }
  );
  const payload = unwrap(res);
  return { consent: payload || {} };
}

async function revokePhotoConsent(clientId) {
  await axios.delete(API.CLIENT_PHOTO_CONSENT + clientId + "/consent", {
    headers: headers(),
  });
  return { success: true };
}

async function fetchSalonTags() {
  const res = await axios.get(API.CLIENT_PHOTO_SALON_TAGS, { headers: headers() });
  const payload = unwrap(res);
  // BE returns [{ tag, count }, ...] — map to FE shape { name, count }.
  const list = Array.isArray(payload) ? payload : [];
  return {
    tags: list.map((r) => ({ name: r.tag || r.name || "", count: r.count || 0 })),
  };
}

async function fetchTagsForService(serviceName) {
  if (!serviceName) return { service: null, tags: [] };
  const res = await axios.get(API.CLIENT_PHOTO_TAGS_FOR_SERVICE, {
    headers: headers(),
    params: { service: serviceName },
  });
  const payload = unwrap(res);
  const list = Array.isArray(payload) ? payload : [];
  return {
    service: serviceName,
    tags: list.map((r) => ({ name: r.tag || r.name || "", count: r.count || 0 })),
  };
}

// 'none' | 'noFace' | 'social': the photo's own recorded answer, else the
// client-wide one. The server sends shareConsentEffective; this re-derives it
// after an optimistic change. `consent` is the client's (the list's consent,
// or a salon-search row's clientConsent).
export const effectiveShareConsent = (photo, consent) => {
  if (!photo) return "none";
  if (photo.shareConsentAt && ["none", "noFace", "social"].includes(photo.shareConsent)) {
    return photo.shareConsent;
  }
  const clientConsent = consent || photo.clientConsent;
  if (clientConsent) return clientConsent.social === true ? "social" : "none";
  return photo.shareConsentEffective || "none";
};

const withId = (p) => (p && p._id ? { ...p, id: p._id } : p);

async function setShareConsent(photoId, value) {
  const res = await axios.put(
    API.CLIENT_PHOTO_SHARE_CONSENT + photoId + "/share-consent",
    { value },
    { headers: headers() }
  );
  return { photo: withId(unwrapStrict(res)) };
}

async function setSocialConsent(clientId) {
  const res = await axios.post(
    API.CLIENT_PHOTO_SOCIAL_CONSENT + clientId + "/social-consent",
    {},
    { headers: headers() }
  );
  return { consent: unwrapStrict(res) || {} };
}

async function revokeSocialConsent(clientId) {
  const res = await axios.delete(API.CLIENT_PHOTO_SOCIAL_CONSENT + clientId + "/social-consent", {
    headers: headers(),
  });
  return { consent: unwrapStrict(res) || {} };
}

async function searchSalonPhotos({ tag = null, consent = null, cursor = null, limit = 30 } = {}) {
  const params = { limit };
  if (tag) params.tag = tag;
  if (consent) params.consent = consent;
  if (cursor) params.cursor = cursor;
  const res = await axios.get(API.CLIENT_PHOTO_SALON, { headers: headers(), params });
  const payload = unwrapStrict(res);
  const rows = Array.isArray(payload.rows) ? payload.rows : [];
  return { photos: rows.map(withId), nextCursor: encodeCursor(payload.nextCursor) };
}

export const clientPhotosService = {
  fetchClientPhotos,
  uploadClientPhoto,
  patchClientPhoto,
  deleteClientPhoto,
  setPhotoConsent,
  revokePhotoConsent,
  fetchSalonTags,
  fetchTagsForService,
  setShareConsent,
  setSocialConsent,
  revokeSocialConsent,
  searchSalonPhotos,
};
